import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { profileService } from '../services/profileService';
import {
  UserProfile,
  UpdateProfileRequest,
  ChangePasswordRequest,
} from '../types/profile.types';
import { handleApiError } from '@/lib/utils/errorHandler';
import { useAuthStore } from '../store/authStore';

export const useProfile = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const { logout } = useAuthStore();

  const fetchProfile = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await profileService.getProfile();
      setProfile(data);
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateProfile = useCallback(
    async (data: UpdateProfileRequest): Promise<boolean> => {
      setIsUpdating(true);
      try {
        const updatedProfile = await profileService.updateProfile(data);
        setProfile(updatedProfile);
        toast.success('Profile updated successfully');
        return true;
      } catch (err) {
        handleApiError(err);
        return false;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  const changePassword = useCallback(
    async (data: ChangePasswordRequest): Promise<boolean> => {
      setIsChangingPassword(true);
      try {
        await profileService.updatePassword(data);
        toast.success('Password changed successfully. Please log in again.');
        // Logout after password change
        setTimeout(() => {
          logout();
        }, 2000);
        return true;
      } catch (err) {
        handleApiError(err);
        return false;
      } finally {
        setIsChangingPassword(false);
      }
    },
    [logout]
  );

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return {
    profile,
    isLoading,
    isUpdating,
    isChangingPassword,
    fetchProfile,
    updateProfile,
    changePassword,
  };
};
